/**
 * 🔑 THE CHOKE POINT that keeps a half-typed inline edit alive on an order list.
 *
 * The inline editor lives inside the row, and the row is rendered from a
 * derived array — so ANYTHING that removes the row from that array unmounts the
 * editor, and `OrderInlineEditor`'s unmount cleanup then clears the page's
 * `editDirtyId`, leaving no stuck state to tip the user off.
 *
 * On `DepartmentOrders.js` six controls could do it: the status pills,
 * Actionable/All, the production filter, search, sort and the page buttons —
 * plus the effect that resets to page 1 on a filter change, which killed a
 * dirty row on page 3 from a pill click even though the row still matched.
 * On `Approvals.js` there is exactly one — the search box — because that page
 * has no pills, no All/Actionable and no pagination. **One is enough**: typing
 * in the search box is not an instruction to throw an edit away.
 *
 * ── WHY PINNING RATHER THAN N GUARDS ──────────────────────────────────
 * CLAUDE.md: *"prefer removing the cause over defending against its symptoms"*.
 * Guarding the six leaves the seventh unguarded the day someone adds it, and
 * every one of them funnels through this one array, so pinning here covers a
 * control that does not exist yet.
 *
 * It is also the right ANSWER, not merely the cheapest place. None of those
 * controls is destructive *by intent* — a user clicking a status pill or typing
 * a search has expressed no wish to discard an edit — so blocking the click
 * (the treatment the navigations correctly get, because leaving IS their
 * purpose) would refuse a harmless action to protect against a
 * side effect. Pinning lets the filter do exactly what was asked and keeps the
 * edit.
 *
 * ── WHAT A PINNED ROW LOOKS LIKE ──────────────────────────────────────
 * Deliberately no new marker. The row's editor is open and the amber
 * "⚠ Unsaved changes" chip is inside it. On Department Orders every OTHER row
 * additionally carries the documented `opacity-40 pointer-events-none` dimming,
 * so the pinned row is the only lit row on the page; **Approvals deliberately
 * does NOT dim** (see the sanctioned-exception box in CLAUDE.md), so there the
 * chip is the marker. An invented fourth signal would be noise either way.
 *
 * ── POSITION ──────────────────────────────────────────────────────────
 * Pinning fires ONLY when the row is absent from the page. A row still in the
 * slice is left exactly where the sort put it (it may move under the cursor on
 * a re-sort; the panel travels with it). A row that has been filtered, searched
 * or paged away has no natural position to keep — it is not in the filtered set
 * at all, or not on this page — so it goes to the top, where an open editor is
 * least likely to be scrolled past.
 *
 * Sourced from the FULL list, not the filtered one: the whole point is the case
 * where the filter has excluded it. Returns the input array unchanged whenever
 * nothing needs pinning, so the common path allocates nothing.
 *
 * 🔀 **Lived in `pages/DepartmentOrders.js` until 2026-08-08**, exported for
 * direct testing. It moved here the day `Approvals.js` gained the same inline
 * editor and therefore the same hazard — a page importing a helper out of
 * another page is how two near-copies start. `DepartmentOrders.js` re-exports
 * the name so its existing citations keep resolving.
 *
 * @param {Array}  pageRows   the rows about to be rendered (post-filter, post-slice)
 * @param {Array}  allOrders  the unfiltered list the dirty row may still be in
 * @param {number|string|null|undefined} dirtyId  the row holding unsaved edits
 * @param {(row) => *} [keyOf]  the row's identity — `o.id` by default (every
 *   order page, byte-identical); the hires Table (2026-09-27) passes
 *   `row => row.item_id`, because a hire LINE has no `id`.
 * @returns {Array} `pageRows`, or `[dirtyRow, ...pageRows]`
 */
const byOrderId = (o) => o.id;

export function withDirtyRowPinned(pageRows, allOrders, dirtyId, keyOf = byOrderId) {
  if (dirtyId === null || dirtyId === undefined) return pageRows;
  if (pageRows.some(o => keyOf(o) === dirtyId)) return pageRows;
  const pinned = allOrders.find(o => keyOf(o) === dirtyId);
  return pinned ? [pinned, ...pageRows] : pageRows;
}

export default withDirtyRowPinned;
